/**
 * PrimeVue Select/MultiSelect enhancement: when the filter narrows the options to
 * exactly one enabled result, pressing Enter (in the filter input) selects it.
 *
 * PrimeVue's Select/MultiSelect has no built-in for this: `onEnterKey` selects the
 * option at `focusedOptionIndex`, but `onFilterChange` resets it to -1 on every
 * keystroke, so Enter with a single filtered result just closes the overlay.
 *
 * The Select's overlay — which contains the filter input — is teleported to the body
 * via `<Portal>`, so the filter input's keydown does not bubble through the component
 * root. The handler is therefore registered as a document-level CAPTURE listener
 * (fires before PrimeVue's own `onFilterKeyDown` on the input). When exactly one
 * enabled option is visible it calls the component's own `onOptionSelect` — the same
 * path a click uses — and stops the event so the native Enter handling never runs.
 *
 * Works for flat and grouped option lists and for both Select and MultiSelect. The
 * surface is the subset of the component instance this needs; it is read off the
 * template ref at event time (Options API instances expose their internals).
 */

import { onMounted, onUnmounted } from "vue";

export interface SelectSingleResultSurface {
  /** Current filter input value (empty/null when no filter is active). */
  filterValue: string | null;
  /** Present when options are grouped (string field or accessor). */
  optionGroupLabel?: string | ((option: unknown) => string) | undefined;
  /** The filtered options: flat array, or array of groups with filtered children. */
  visibleOptions: unknown[];
  /** Component's own select/toggle path (same one a click uses). */
  onOptionSelect: (event: KeyboardEvent, option: unknown, isHide?: boolean) => void;
  isOptionDisabled: (option: unknown) => boolean;
  getOptionGroupChildren: (group: unknown) => unknown[];
  /** Select wraps the filter in an InputText component ($el = input); MultiSelect's
   * filter is a raw <input>. Both resolve via the normalization below. */
  $refs?: {
    filterInput?: { $el?: HTMLElement } | HTMLElement;
  };
}

/**
 * Pure handler factory (unit-testable without a component instance). Reads the
 * instance lazily at event time.
 */
export const createSingleResultFilterEnterHandler = (
  getInstance: () => SelectSingleResultSurface | null,
): ((event: KeyboardEvent) => void) => {
  return (event: KeyboardEvent): void => {
    const instance = getInstance();
    if (!instance) {
      return;
    }
    if (event.isComposing) {
      return;
    }
    const { code } = event;
    if (code !== "Enter" && code !== "NumpadEnter") {
      return;
    }

    const filterRef = instance.$refs?.filterInput;
    const filterEl = filterRef instanceof HTMLElement ? filterRef : filterRef?.$el;
    if (!filterEl || !filterEl.contains(event.target as Node)) {
      return;
    }
    if (!instance.filterValue) {
      return;
    }

    const leaves = instance.optionGroupLabel
      ? instance.visibleOptions.flatMap((group) => instance.getOptionGroupChildren(group))
      : instance.visibleOptions;
    const enabled = leaves.filter((option) => !instance.isOptionDisabled(option));
    if (enabled.length !== 1) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    instance.onOptionSelect(event, enabled[0] as never, true);
  };
};

/**
 * Wire the single-result Enter behavior to a wrapped Select/MultiSelect. Registers
 * the document-level capture listener for the component's lifetime.
 */
export const useSingleResultFilterEnter = (
  getInstance: () => SelectSingleResultSurface | null,
): void => {
  const handler = createSingleResultFilterEnterHandler(getInstance);
  onMounted(() => document.addEventListener("keydown", handler, true));
  onUnmounted(() => document.removeEventListener("keydown", handler, true));
};
